/**
 * Adaptive Segment-icon color (WL-012, deck `adaptive-icons`).
 *
 * Gordon's rule (concepts/adaptive-icons `02-white-default.html`, verbatim):
 * "Segment icons should DEFAULT to WHITE, only be dark when needed. Dark should
 * be on-brand dark blue." Icons switch to brand navy `#123051` ONLY where a
 * light fill makes white fail - contrast(white, fill) < 2.0.
 *
 * Row-01 also documents a stricter "whichever wins WCAG contrast" variant; both
 * are provided (`mode`), white-default being canonical per the later note.
 *
 * Pure. Delegates the luminance/contrast math to the existing, tested
 * `src/utils/contrast.js` (no duplicate WCAG implementation).
 */

import { contrastRatio } from "../../../../../utils/contrast";

/** Brand tokens for the two icon inks. */
export const ICON_WHITE = "#FFFFFF";
export const ICON_NAVY = "#123051"; // on-brand dark blue

/**
 * The white-default threshold: white stays unless its contrast against the fill
 * drops below this (i.e. the fill is light enough that white washes out).
 * 2.0 per Gordon's note on `02-white-default`.
 */
export const WHITE_DEFAULT_MIN_CONTRAST = 2.0;

/**
 * Pick the adaptive icon color for a Segment fill.
 *
 * @param {string} fillHex the Segment fill color (hex). For non-solid fills
 *        (gradient/pattern/image) pass a representative/dominant hex, or omit to
 *        default to white.
 * @param {{ mode?: 'white-default'|'best-contrast', white?:string, dark?:string,
 *          threshold?:number }} [opts]
 * @returns {{ color:string, isDark:boolean, reason:string }}
 */
export const adaptiveIconColor = (fillHex, opts = {}) => {
  const white = opts.white || ICON_WHITE;
  const dark = opts.dark || ICON_NAVY;
  const mode = opts.mode || "white-default";
  const threshold = opts.threshold ?? WHITE_DEFAULT_MIN_CONTRAST;

  const whiteRatio = contrastRatio(white, fillHex);
  // Un-evaluable fill (non-hex / gradient) -> default white per the rule.
  if (whiteRatio == null) {
    return { color: white, isDark: false, reason: "non-solid-fill-default-white" };
  }

  if (mode === "best-contrast") {
    const darkRatio = contrastRatio(dark, fillHex) ?? 0;
    const useDark = darkRatio > whiteRatio;
    return {
      color: useDark ? dark : white,
      isDark: useDark,
      reason: useDark ? "best-contrast-navy" : "best-contrast-white",
    };
  }

  // white-default (canonical): navy ONLY when white fails the threshold.
  const useDark = whiteRatio < threshold;
  return {
    color: useDark ? dark : white,
    isDark: useDark,
    reason: useDark
      ? `white-fails-${threshold}-use-navy`
      : "white-default",
  };
};

/**
 * Convenience: map an array of Segment fills to their adaptive icon colors.
 * @param {Array<{color?:string}|string>} segments
 * @param {object} [opts] forwarded to `adaptiveIconColor`
 * @returns {Array<{color:string,isDark:boolean,reason:string}>}
 */
export const adaptiveIconColors = (segments, opts) =>
  (Array.isArray(segments) ? segments : []).map((s) =>
    adaptiveIconColor(typeof s === "string" ? s : s && s.color, opts)
  );
